import { Suspense, useState, useEffect } from 'react'
import Section from './_Section'
import { createMarkup } from '../../helpers/markup'
import ButtonEl from '../helpers/ctas/Button'
import Loader from '../helpers/Loader'
import themeConfig from '../../../theme.config'
import ReactPaginate from 'react-paginate'
import { ArrowLongLeftIcon, ArrowLongRightIcon } from '@heroicons/react/24/solid'
import { transformPaddingToTailwind } from '../../helpers/style'

async function getResults(searchTerm) {
  try {
    const response = await fetch(`${window.location.origin}/umbraco/v1/searchapi/search?key=${searchTerm}`)
    const responseJson = await response.json()
    return responseJson
  } catch (error) {
    console.error(error)
  }
}

const ResultsWrapper = (props) => {
  const [results, setResults] = useState(null)
  const [resultsText, setResultsText] = useState('Found {x} results for {keyword}')
  const [searchTerm, setSearchTerm] = useState()
  const [totalPages, setTotalPages] = useState(1)
  // Here we use item offsets; we could also use page offsets
  // following the API or data you're working with.
  const [itemOffset, setItemOffset] = useState(0)

  // Simulate fetching items from another resources.
  // (This could be items from props; or items loaded in a local state
  // from an API endpoint with useEffect and useState)
  const endOffset = itemOffset + props.pageSize
  const currentItems = (results?.result?.length > 0 && results?.result.slice(itemOffset, endOffset)) || []

  useEffect(() => {
    if (props.pageSize > 0) {
      const size = Math.ceil(results?.result?.length / props.pageSize)
      setTotalPages(() => (!isNaN(size) ? size : 0))
    }
  }, [results])

  useEffect(() => {
    if (!props.searchTerm) return

    const results = getResults(props.searchTerm)
    results.then((response) => {
      if (!response.errorMessage) {
        setResults(response)
      }
    })

    setResultsText((prevState) => props.searchResultText || prevState)
  }, [props.searchTerm])

  const submitForm = (evt) => {
    evt.preventDefault()
    const loc = window.location
    const searchUrl = new URL(`${loc.origin}/${themeConfig.settings.searchPageStub}`)
    searchTerm && searchTerm?.length > 0 && searchUrl.searchParams.append('q', searchTerm)
    window.location = searchUrl
  }

  const handlePageClick = (event) => {
    const newOffset = (event.selected * props.pageSize) % results?.result?.length
    setItemOffset(newOffset)
  }

  return (
    <>
      <div className="my-4">
        <form role="search" className="flex flex-wrap md:flex-nowrap gap-4" onSubmit={submitForm}>
          <input
            id="in-page-search"
            type="text"
            onChange={(e) => setSearchTerm(encodeURIComponent(e.target.value))}
            placeholder={props.placeholderText}
            autoComplete="off"
            aria-label="Sitewide"
            className="grow rounded-full py-3 px-5 max-w-[100%] basis-auto grow"
          />
          <ButtonEl theme="secondary" outerClass="basis-auto grow-0" type="submit">
            {props.searchButtonText}
          </ButtonEl>
        </form>
      </div>
      {resultsText && (
        <>
          {results && !results.success && (
            <p className="text-2xl mb-8">
              {resultsText
                .replace('{x}', 0)
                .replace(
                  '{keyword}',
                  `"${decodeURIComponent(props.searchTerm !== 'undefined' ? `${props.searchTerm}` : '')}"`,
                )}
            </p>
          )}
          {results && results.success && (
            <p className="text-2xl mb-8">
              {resultsText
                .replace('{x}', results?.result?.length || 0)
                .replace(
                  '{keyword}',
                  `"${decodeURIComponent(props.searchTerm !== 'undefined' ? `${props.searchTerm}` : '')}"`,
                )}
            </p>
          )}

          <ul className="divide-y">
            {currentItems?.map((res, idx) => {
              return <ResultsItem key={`result-${idx}`} result={res} idx={idx} />
            })}
          </ul>

          {results?.result?.length > props.pageSize && (
            <ReactPaginate
              breakLabel="..."
              previousLabel={
                <>
                  <span className="sr-only">Previous</span>
                  <ArrowLongLeftIcon role="none" className="h-6 w-6" aria-hidden="true" />
                </>
              }
              onPageChange={handlePageClick}
              pageRangeDisplayed={props.pageSize}
              pageCount={totalPages}
              nextLabel={
                <>
                  <span className="sr-only">Next</span>
                  <ArrowLongRightIcon role="none" className="h-6 w-6" aria-hidden="true" />
                </>
              }
              renderOnZeroPageCount={null}
            />
          )}
        </>
      )}
    </>
  )
}

const ResultsItem = (props) => {
  const [onPage, setOnPage] = useState(false)
  useEffect(() => {
    setOnPage(() => {
      return props.idx >= props.startIndex && idx <= props.endIndex
    })
  }, [props.startIndex, props.endIndex])

  return (
    <>
      <li className="py-8" data-idx={props.idx}>
        <a href={props.result?.url} className="inline-block mb-4">
          <strong className="text-xl text-secondary-500 inline-block mb-0">
            {props.result?.title ?? props.result?.nodeName}
          </strong>
        </a>
        {props.result?.description && (
          <p data-rte="true" dangerouslySetInnerHTML={createMarkup(props.result?.description)}></p>
        )}
      </li>
    </>
  )
}

const SearchResult = (props) => {
  const [searchTerm, setSearchTerm] = useState()
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  useEffect(() => {
    const url = new URL(window.location)
    const search = new URLSearchParams(url.search)
    setSearchTerm(search.get('q'))
  }, [])

  return (
    <Section type="searchResult" outerClass={componentPadding} innerClass="px-7" {...props}>
      <Suspense key={`search-loader`} fallback={<Loader />}>
        <ResultsWrapper {...props} searchTerm={searchTerm} />
      </Suspense>
    </Section>
  )
}

export default SearchResult
